/**
 * Centralized Input Validation
 *
 * Shared validators for user inputs across the frontend.
 * Prevents DRY violations and ensures consistent sanitization.
 */

/**
 * Sanitize stock symbol to prevent injection attacks.
 * Only allows alphanumeric characters, dots, hyphens, and carets.
 * Returns null if input is invalid or potentially malicious.
 *
 * Valid examples: AAPL, BRK.A, BRK-B, ^GSPC, 0700.HK
 */
export function sanitizeSymbol(input: string): string | null {
  if (!input || typeof input !== 'string') return null;

  const trimmed = input.trim().toUpperCase();

  // Only allow valid stock symbol characters
  const sanitized = trimmed.replace(/[^A-Z0-9.\-^]/g, '');

  if (sanitized.length === 0 || sanitized.length > 20) return null;

  // Block common injection patterns (SQL, NoSQL, command injection, XSS)
  const injectionPatterns = /('|"|;|--|\/\*|\*\/|`|\$\{|\$\(|<|>|&|\||\\|union|select|drop|insert|delete|update|exec|xp_)/i;
  if (injectionPatterns.test(input)) return null;

  return sanitized;
}

/**
 * Validate a numeric price input.
 * Returns null if invalid, otherwise the parsed number.
 */
export function sanitizePrice(input: string | number): number | null {
  const num = typeof input === 'string' ? parseFloat(input) : input;
  if (!Number.isFinite(num) || num <= 0) return null;
  return num;
}

/**
 * Sanitize a general text input (names, labels, etc.).
 * Strips HTML tags and trims whitespace. Returns null if empty or too long.
 */
export function sanitizeText(input: string, maxLength = 200): string | null {
  if (!input || typeof input !== 'string') return null;
  const stripped = input.replace(/<[^>]*>/g, '').trim();
  if (stripped.length === 0 || stripped.length > maxLength) return null;
  return stripped;
}

/**
 * Extract a human-readable error message from an unknown caught value.
 * Use in catch blocks instead of `err instanceof Error ? err.message : String(err)`.
 */
export function getErrorMessage(err: unknown, fallback = 'An unexpected error occurred'): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return fallback;
}
